import { useEffect, useRef } from 'react';

import type { ProductKeys } from '../../../common/constants/products';
import { onRecommendationsServed } from '../../../common/utils/events/recommendations-events';
import { useAnalytics } from '../index';

// Module-level flag so the event fires at most once per page session,
// even if the host component unmounts and remounts (e.g. Relay suspense).
let hasFiredThisSession = false;

/**
 * Reset the module-level fired state.
 * Exposed exclusively for tests — production code should never call this.
 */
export function resetVisibleProductsToAuthShownFlag(): void {
	hasFiredThisSession = false;
}

export function useFireVisibleProductsToAuthShown(
	visibleProductsToAuth: ProductKeys[],
	recommended3pApps: string[],
): void {
	const { fireAnalyticsEvent } = useAnalytics();

	// Keep refs to the latest values so the effect fires with current data
	// without re-triggering on every render.
	const fireAnalyticsEventRef = useRef(fireAnalyticsEvent);
	fireAnalyticsEventRef.current = fireAnalyticsEvent;

	const visibleProductsRef = useRef(visibleProductsToAuth);
	visibleProductsRef.current = visibleProductsToAuth;

	const recommended3pAppsRef = useRef(recommended3pApps);
	recommended3pAppsRef.current = recommended3pApps;

	// Fire exactly once per page session, and only after recommended3pApps
	// has been populated (the data loads asynchronously).
	useEffect(() => {
		if (hasFiredThisSession || recommended3pApps.length === 0) {
			return;
		}
		hasFiredThisSession = true;
		fireAnalyticsEventRef.current(
			onRecommendationsServed({
				visibleProductsToAuth: visibleProductsRef.current,
				recommended3pApps: recommended3pAppsRef.current,
			}),
		);
	}, [recommended3pApps]);
}
